import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApiClientError,
  HandleKindSchema,
  type ApiClientErrorCode,
  type Handle,
  type HandleKind,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { HandleCardView, type HandleCardState } from './HandleCardView';
import { useHandleImageUrls } from './useHandleImageUrls';

/**
 * Container Экрана 2.4.3 (см. ADR-0005). Загружает Handle по globalId из URL,
 * различает 404 (not-found) и прочие ошибки (error).
 */
export function HandleCard() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string; globalId: string }>();
  const kindParse = HandleKindSchema.safeParse(params.kind);
  const kind: HandleKind = kindParse.success ? kindParse.data : 'handle';
  const globalId = params.globalId ?? '';

  const [data, setData] = useState<Handle | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    setNotFound(false);
    setError(null);
    let cancelled = false;
    apiClient.handles.get(globalId).then(
      (res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.status === 404) {
          setNotFound(true);
        } else {
          // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
          const apiErr =
            err instanceof ApiClientError
              ? { code: err.code, message: err.message }
              : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
          setError(apiErr);
        }
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [globalId]);

  const state = useMemo<HandleCardState>(() => {
    if (loading) return 'loading';
    if (notFound) return 'not-found';
    if (error) return 'error';
    if (data) return 'success';
    return 'loading';
  }, [loading, notFound, error, data]);

  // Ф-Фронт (ADR-0070): signed-URL для hero-фото (первичный ключ = photoKeys[0]).
  const heroKey = data?.photoKeys[0];
  const heroKeys = useMemo(() => [heroKey], [heroKey]);
  const { urls: imageUrls } = useHandleImageUrls(heroKeys);
  const heroImageUrl = heroKey ? (imageUrls[heroKey] ?? null) : null;

  /**
   * «Скопировать сообщение» из меню пересылки: единый текст пересылки в буфер
   * обмена (clipboard), с alert-уведомлением. Клон onCopyMessage декора;
   * clipboard недоступен (insecure context) → fallback alert(text).
   */
  async function onCopyMessage(text: string) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        alert('Сообщение скопировано');
        return;
      } catch {
        // clipboard недоступен (insecure context / permission) → fallback ниже
      }
    }
    alert(text);
  }

  return (
    <HandleCardView
      state={state}
      data={data ?? undefined}
      error={error ?? undefined}
      heroImageUrl={heroImageUrl}
      onShopsClick={() => navigate(`/handles/${kind}/${encodeURIComponent(globalId)}/shops`)}
      onCopyMessage={onCopyMessage}
      onBackToCatalog={() => navigate(`/handles/${kind}`)}
    />
  );
}
